import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Drawer, Form, Space, message, Image, Upload, Switch, Alert } from 'antd';
import { drawerWidth, drawerFormItemLayout } from '@utils/property';
import { fileApi } from '@services/Conference';
import { updateGuestJourneySettings } from '@services/Guest';
import './index.less';
import config from '@config';
import { prepareBCOSSUrl } from '@utils/utils';
import TinymceEditor from '@/components/Conference/components/Editor/TinyMCEEditor';

// 行程与日程提醒设置
const GuestJourneySetting = ({ visibleItineraryScheduleDrawer, setVisibleItineraryScheduleDrawer, conference, getConferenceInfo }) => {
    const optionHeaders = { headers: { 'Content-Type': 'multipart/form-data' } };

    const params = useParams();
    const conferenceId = params?.id || 0; // 会议项目 ID

    const [form] = Form.useForm();

    const isAllowCheck = useRef(null);
    const [agendaLoading, setAgendaLoading] = useState(false); // 新增/编辑日程loading
    const [bgImgList, setBgImgList] = useState([]);

    const journey_enabled = Form.useWatch('journey_enabled', form);
    const agenda_enabled = Form.useWatch('agenda_enabled', form);

    const closeDrawer = () => {
        setVisibleItineraryScheduleDrawer(false);
        form.resetFields();
        setBgImgList([]);
    };

    //上传头像
    const checkout = (file, size) => {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
        // 判断类型
        if (!isJpgOrPng) {
            message.error('仅支持上传JPG/PNG格式的图片。');
            isAllowCheck.current = true;
            return false;
        } else {
            isAllowCheck.current = false;
        }

        // 判断大小
        const isLt5M = file.size / 1024 / 1024 < size;
        if (!isLt5M) {
            message.error(`图片不能大于${size}MB。`);
            isAllowCheck.current = true;
            return false;
        } else {
            isAllowCheck.current = false;
        }
        return isJpgOrPng && isLt5M;
    };

    const imageUpload = info => {
        const formData = new FormData();
        formData.append('file', info.file);
        formData.append('type', 14);
        if (!isAllowCheck.current) {
            fileApi(formData, optionHeaders).then(res => {
                if (!res) {
                    message.error('上传文件失败！');
                    return false;
                }
                if (res.status_code === 200) {
                    setBgImgList([getFileUrl(res.data)]);
                    form.setFieldValue('background_img_url', res.data);
                    form.setFields([{ name: 'background_img_url', errors: null }]);
                }
            });
        } else {
            form.setFieldValue('background_img_url', undefined);
        }
    };

    // 获取上传文件 - 前缀
    const getFileUrl = url => {
        if (url && url.includes('https')) {
            return url;
        }
        if (url && !url.includes('https')) {
            return config.OSS_PREFIX + url;
        }
        return null;
    };

    // 提交行程单设置，新增/编辑
    const submitJourney = () => {
        form.validateFields()
            .then(async values => {
                const res = await updateGuestJourneySettings({
                    id: conferenceId,
                    guestGuideSettings: JSON.stringify(values),
                });
                setAgendaLoading(false);
                if (res.status_code === 200) {
                    message.success('行程与日程提醒设置成功');
                    setVisibleItineraryScheduleDrawer(false);
                    getConferenceInfo(conferenceId);
                }
            })
            .catch(err => {
                console.log('提交失败', err);
            });
    };

    useEffect(() => {
        if (conference?.guestJourneySettings) {
            const guestGuideSettings = JSON.parse(conference.guestGuideSettings);
            form.setFieldsValue(guestGuideSettings);
            if (guestGuideSettings?.background_img_url) {
                setBgImgList([getFileUrl(guestGuideSettings?.background_img_url)]);
            }
        }
    }, [visibleItineraryScheduleDrawer, conference]);

    return (
        <>
            <Drawer
                closable={{ placement: 'end' }}
                title="Word版嘉宾行程与日程设置"
                size={drawerWidth}
                layout="horizontal"
                open={visibleItineraryScheduleDrawer}
                onClose={closeDrawer}
                footer={
                    <div className="custom-drawer-footer" style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={closeDrawer}>取消</Button>
                            <Button type="primary" loading={agendaLoading} onClick={submitJourney}>
                                确定
                            </Button>
                        </Space>
                    </div>
                }
            >
                <Alert title="Word嘉宾行程与日程设置，为嘉宾管理中有任务安排嘉宾设置统一的生成模板。" type="warning" style={{ marginBottom: '20px' }} />
                <div className="guest-journey-form">
                    <Form
                        name="basic"
                        form={form}
                        autoComplete="off"
                        labelCol={{
                            span: 3,
                        }}
                        wrapperCol={{
                            span: 21,
                        }}
                    >
                        <Form.Item label="温馨提示" name="opening_script" rules={[{ required: true }]}>
                            <TinymceEditor form={form} fieldValue="opening_script" id="opening_script" initialValue={form.getFieldValue('opening_script')} placeholder="" />
                        </Form.Item>

                        <Form.Item label="行程安排" name="journey_enabled" getValueFromEvent={value => (value ? 1 : 0)} valuePropName="checked">
                            <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                        </Form.Item>

                        {!!journey_enabled && (
                            <Form.Item label="行程提醒" name="journey_guide" getValueFromEvent={value => (value ? 1 : 0)}>
                                <TinymceEditor form={form} fieldValue="journey_guide" id="journey_guide" initialValue={form.getFieldValue('journey_guide')} placeholder="" />
                            </Form.Item>
                        )}

                        <Form.Item label="日程安排" name="agenda_enabled" getValueFromEvent={value => (value ? 1 : 0)} valuePropName="checked">
                            <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                        </Form.Item>

                        {!!agenda_enabled && (
                            <Form.Item label="日程提醒" name="agenda_guide">
                                <TinymceEditor form={form} fieldValue="agenda_guide" id="agenda_guide" initialValue={form.getFieldValue('agenda_guide')} placeholder="" />
                            </Form.Item>
                        )}

                        <Form.Item name="background_img_url" label="文档背景图" extra="图片宽210mm高297mm，页眉元素不高于25mm，300PPI，支持JPG/PNG格式，大小不超过5MB" className="head-image">
                            {bgImgList.length > 0 && (
                                <div className="uploaded-img-box" style={{ width: 210, height: 297 }}>
                                    <Image src={bgImgList[0]} alt="行程背景图预览" />
                                    <span
                                        className="btn-remove"
                                        onClick={() => {
                                            setBgImgList([]);
                                            form.setFieldValue('background_img_url', '');
                                        }}
                                    >
                                        <i className="bi bi-x"></i>
                                    </span>
                                </div>
                            )}

                            <div>
                                <Upload beforeUpload={file => checkout(file, 5)} fileList={[]} customRequest={imageUpload} maxCount={1}>
                                    <Button icon={<i className="bi bi-upload"></i>}>{bgImgList.length > 0 ? '更改图片' : '上传图片'}</Button>
                                </Upload>
                            </div>
                        </Form.Item>
                    </Form>
                </div>
            </Drawer>
        </>
    );
};
export default GuestJourneySetting;
